import type { NaturalGasNarrativeInput } from './naturalGasConsumptionNarrative';
import {
  compareToReference,
  formatComparatorPhrase,
  isFiniteNumber,
} from './narrativeFormatting';
import { estimatedYearsKeyInsightsSentence } from './energyEstimatedYearsCopy';
import { buildTrendSentence } from './optionalClauseTokens';
import type { NarrativeTokenMap } from './narrativeTemplateEngine';

function formatThermsHeadline(therms: number): string {
  if (!Number.isFinite(therms) || therms <= 0) return '';
  if (therms >= 1_000_000) {
    return `${(therms / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} million`;
  }
  return therms.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function compositionSentence(resShare: number | null, comShare: number | null): string {
  if (resShare == null || comShare == null) return '';
  return ` Residential accounts represented ${resShare.toFixed(1)}% of the displayed total and commercial accounts ${comShare.toFixed(1)}%.`;
}

export function buildNaturalGasNarrativeTokens(input: NaturalGasNarrativeInput): NarrativeTokenMap {
  const estimatedYearsSentence = estimatedYearsKeyInsightsSentence(input.hasEstimatedYears);

  const trendSentence = buildTrendSentence(input.percentChange, input.baselineYear);

  const base: NarrativeTokenMap = {
    locationName: input.locationName,
    latestYear: input.latestYear,
    baselineYear: input.baselineYear,
    estimatedYearsSentence,
    trendSentence,
    trendPhrase: '',
    percentChange: input.percentChange,
  };

  if (input.viewId === 'perCapita') {
    const local = input.localThermsPerPerson;
    let californiaComparisonSentence = '';
    if (
      input.comparisonCompatibility === 'compatible' &&
      input.comparisonYear != null &&
      isFiniteNumber(local) &&
      isFiniteNumber(input.californiaThermsPerPerson)
    ) {
      const cmp = compareToReference(local, input.californiaThermsPerPerson);
      if (cmp.relation !== 'unavailable') {
        californiaComparisonSentence = ` Compared with ${Math.round(input.californiaThermsPerPerson)} therms per resident statewide in ${input.comparisonYear} (${formatComparatorPhrase(cmp.relation, 'California')}).`;
      }
    }
    return {
      ...base,
      localThermsPerPerson: isFiniteNumber(local) ? Math.round(local) : null,
      californiaComparisonSentence,
    };
  }

  if (input.viewId === 'total') {
    const totalToken = input.totalTherms != null ? formatThermsHeadline(input.totalTherms) : null;
    const shareBreakdownSentence =
      input.residentialShare != null && input.commercialShare != null
        ? ` Residential accounts represented ${input.residentialShare.toFixed(1)}% of the displayed total and commercial accounts ${input.commercialShare.toFixed(1)}%.`
        : '';
    return {
      ...base,
      totalTherms: totalToken,
      residentialShare: input.residentialShare != null ? input.residentialShare.toFixed(1) : null,
      commercialShare: input.commercialShare != null ? input.commercialShare.toFixed(1) : null,
      shareBreakdownSentence,
      compositionSentence: '',
    };
  }

  if (input.viewId === 'res') {
    const shareClause =
      input.residentialShare != null
        ? ` Residential accounts represented ${input.residentialShare.toFixed(1)}% of the residential-plus-commercial total in the common year.`
        : '';
    return {
      ...base,
      residentialTherms: input.residentialTherms != null ? formatThermsHeadline(input.residentialTherms) : null,
      residentialShare: input.residentialShare != null ? input.residentialShare.toFixed(1) : null,
      compositionSentence: shareClause,
    };
  }

  const shareClause =
    input.commercialShare != null
      ? ` Commercial accounts represented ${input.commercialShare.toFixed(1)}% of the residential-plus-commercial total in the common year.`
      : '';
  return {
    ...base,
    commercialTherms: input.commercialTherms != null ? formatThermsHeadline(input.commercialTherms) : null,
    commercialShare: input.commercialShare != null ? input.commercialShare.toFixed(1) : null,
    compositionSentence: shareClause,
  };
}
